/**
 * Theme storage and application. index.html runs the same logic inline before first paint so the
 * page never flashes the wrong theme; keep the two in step (key, values, class, color-scheme).
 */
export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'tr-theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

export function readThemePreference(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function writeThemePreference(theme: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only.
  }
}

export function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches
}

export function resolveTheme(pref: ThemePreference, prefersDark: boolean = systemPrefersDark()): ResolvedTheme {
  return pref === 'system' ? (prefersDark ? 'dark' : 'light') : pref
}

export function applyTheme(resolved: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

/** Calls back whenever the OS theme flips; returns an unsubscribe. */
export function watchSystemTheme(onChange: (prefersDark: boolean) => void): () => void {
  if (typeof matchMedia !== 'function') return () => {}
  const mq = matchMedia(DARK_QUERY)
  const listener = (e: MediaQueryListEvent) => onChange(e.matches)
  mq.addEventListener('change', listener)
  return () => mq.removeEventListener('change', listener)
}
